import DonationPanel from '../components/DonationPanel';
import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, StatCards, Card, TabBar, Table, StatusPill, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES , useBreakpoint } from '../components/UI';
import * as api from '../api/client';
import { printCashierReceipt, printPaymentReceipt } from '../utils/print';
import { KpiCard, AreaChart, BarChartV, DonutRing, RankBars, DashCard, ActivityFeed, Gauge } from '../components/DashboardWidgets';
import { useAuth } from '../context/AuthContext';

const MODE_ICONS  = { cash:'💵', mpesa:'📱', bank:'🏦', pdq:'💳', 'room-charge':'🏨', complimentary:'🎁' };
const MODE_COLORS = { cash:'#2ecc71', mpesa:'#00b300', bank:'#3498db', pdq:'#9b59b6', 'room-charge':'#e67e22', complimentary:'#c8a96e' };

export default function CashierPage({ notify }) {
  const bp = useBreakpoint();
  const { user } = useAuth();
  const [tab, setTab] = useState('queue');
  const [bills, setBills]       = useState([]);
  const [payments, setPayments] = useState([]);
  const [compBills, setCompBills] = useState([]);

  const load = useCallback(async () => {
    const [b, p, cb] = await Promise.all([api.getBills(), api.getPayments(), api.getComplimentaryBills()]);
    setBills(b.data.results || b.data);
    setPayments(p.data.results || p.data);
    setCompBills(cb.data.results || cb.data);
  }, []);
  useEffect(() => { load(); const iv=setInterval(load,10000); return ()=>clearInterval(iv); }, [load]);

  const queue    = bills.filter(b => b.status === 'payment-requested');
  const allBills = bills;
  const todayRev = payments.filter(p=>p.paid_at?.startsWith(new Date().toISOString().slice(0,10))).reduce((s,p)=>s+parseFloat(p.amount||0),0);
  const pendingComp = compBills.filter(c=>c.status==='pending').length;

  const confirm = async (billId, mode, txCode) => {
    await api.confirmPayment(billId, { payment_mode: mode, transaction_code: txCode });
    load();
    notify('Payment confirmed!');
  };

  const canApproveComp = user?.role === 'admin' || user?.role === 'restaurant_manager';

  return (
    <div>
      <PageHeader icon="💳" title="Cashier" sub="Confirm payments & manage billing — auto-refreshes every 10s"/>
      <StatCards stats={[
        { label:'Payment Queue',   value:queue.length,      color:'#e74c3c', icon:'⏳', sub: queue.length === 0 ? 'All clear' : 'Awaiting confirmation' },
        { label:'Revenue Today',   value:fmtKES(todayRev),  color:'var(--accent)', icon:'💰', spark: Array.from({length:7},(_,i)=>{const d=new Date();d.setDate(d.getDate()-6+i);return payments.filter(p=>(p.paid_at||p.created_at||'').slice(0,10)===d.toISOString().slice(0,10)).reduce((s,p)=>s+parseFloat(p.amount||0),0);}), sub:`${payments.filter(p=>(p.paid_at||p.created_at||'').startsWith(new Date().toISOString().slice(0,10))).length} transactions` },
        { label:'Total Collected', value:fmtKES(payments.reduce((s,p)=>s+parseFloat(p.amount||0),0)), color:'#2ecc71', icon:'✅' },
        { label:'All Bills',       value:allBills.length,   color:'#3498db', icon:'📋', sub:`${queue.length} pending` },
      ]}/>
      <TabBar tabs={[
        { id:'queue',        label:`Payment Queue (${queue.length})`, icon:'⏳' },
        { id:'all',          label:'All Bills',    icon:'📋' },
        { id:'completed',    label:'Completed',    icon:'✅' },
        { id:'complimentary',label:`Complimentary${pendingComp>0?` (${pendingComp})`:''}`, icon:'🎁' },
        { id:'pool',         label:'Pool',         icon:'🏊' },
        { id:'donations',    label:'Donations',    icon:'🌿' },
      ]} active={tab} onChange={setTab}/>

      {tab==='queue' && (
        queue.length===0
          ? <Card title="Payment Queue"><Empty text="No pending payment requests — queue is clear"/></Card>
          : <div style={{ display:'grid', gap:12 }}>{queue.map(bill=><CashierBillCard key={bill.id} bill={bill} onConfirm={confirm} notify={notify}/>)}</div>
      )}

      {tab==='all' && (
        <Card title={`All Bills (${allBills.length})`}>
          {allBills.length===0?<Empty text="No bills yet"/>:<Table
            headers={['Bill #','Waiter','Table','Guest','Type','Amount','Mode','Tx Code','Status','Created']}
            rows={[...allBills].reverse().map(b=>[
              <code style={{ fontSize:11 }}>#{b.id}</code>,
              <span style={{ color:'#3498db', fontWeight:600 }}>{b.waiter_name}</span>,
              b.order_table||`#${b.order}`, b.guest_name, b.bill_type,
              <span style={{ color:'var(--accent)', fontWeight:700 }}>{fmtKES(b.total)}</span>,
              b.payment_mode?<span style={{ color:MODE_COLORS[b.payment_mode]||'#aaa', textTransform:'uppercase', fontSize:11 }}>{MODE_ICONS[b.payment_mode]} {b.payment_mode}</span>:<span style={{ color:'var(--textFaint)' }}>—</span>,
              b.transaction_code?<code style={{ fontSize:11, color:'#2ecc71', letterSpacing:1 }}>{b.transaction_code}</code>:<span style={{ color:'var(--textFaint)' }}>—</span>,
              <StatusPill status={b.status}/>,
              <span style={{ fontSize:11, color:'var(--textFaint)' }}>{new Date(b.created_at).toLocaleString('en-KE')}</span>
            ])}
          />}
        </Card>
      )}

      {tab==='completed' && (
        <Card title={`Completed Payments (${payments.length})`}>
          {payments.length===0?<Empty text="No payments yet"/>:<Table
            headers={['Pay #','Waiter','Guest','Type','Amount','Mode','Tx Code','Confirmed By','Paid At','Print']}
            rows={[...payments].reverse().map(p=>[
              <code style={{ fontSize:11 }}>#{p.id}</code>,
              <span style={{ color:'#3498db', fontWeight:600 }}>{p.waiter_name}</span>,
              p.guest_name, p.payment_type,
              <span style={{ color:'#2ecc71', fontWeight:700 }}>{fmtKES(p.amount)}</span>,
              <span style={{ color:MODE_COLORS[p.payment_mode]||'#aaa', textTransform:'uppercase', fontSize:11 }}>{MODE_ICONS[p.payment_mode]} {p.payment_mode}</span>,
              p.transaction_code?<code style={{ fontSize:11, color:'#2ecc71', letterSpacing:1 }}>{p.transaction_code}</code>:<span style={{ color:'var(--textFaint)' }}>—</span>,
              <span style={{ fontSize:12, color:'var(--textMuted)' }}>{p.confirmed_by_name}</span>,
              <span style={{ fontSize:11, color:'var(--textFaint)' }}>{new Date(p.paid_at).toLocaleString('en-KE')}</span>,
              <button style={{ background:'#1a2a3a', border:'none', borderRadius:2, padding:'4px 10px', color:'var(--accent)', fontSize:11, cursor:'pointer' }} onClick={()=>printPaymentReceipt(p)}>🖨</button>
            ])}
          />}
        </Card>
      )}

      {tab==='pool' && <PoolPanel notify={notify} onDone={load}/>}
      {tab==='complimentary' && <ComplimentaryPanel compBills={compBills} bills={bills} notify={notify} onUpdate={load} canApprove={canApproveComp} />}
      {tab==='donations' && <DonationPanel notify={notify} />}
    </div>
  );
}

function CashierBillCard({ bill, onConfirm, notify }) {
  const [overrideMode, setOverrideMode] = useState('');
  const [overrideTx,   setOverrideTx]   = useState('');
  const [loading, setLoading] = useState(false);

  // ── Payment testing state ──────────────────────────────────────────────────
  const [testMode,     setTestMode]     = useState('');  // 'mpesa' | 'stripe' | ''
  const [mpesaPhone,   setMpesaPhone]   = useState('');
  const [mpesaStatus,  setMpesaStatus]  = useState(null);
  const [mpesaPending, setMpesaPending] = useState(false);
  const [stripeCard,   setStripeCard]   = useState('4242 4242 4242 4242');
  const [stripeExp,    setStripeExp]    = useState('12/26');
  const [stripeCvv,    setStripeCvv]    = useState('123');
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripePending,setStripePending]= useState(false);

  const doMpesaTest = async () => {
    setMpesaPending(true); setMpesaStatus(null);
    try {
      const r = await api.mpesaStkPush({ phone: mpesaPhone, amount: bill.total, bill_id: bill.id });
      setMpesaStatus({ ok: true, data: r.data });
      setOverrideMode('mpesa');
      setOverrideTx(r.data.CheckoutRequestID);
      const modeLabel = r.data.mode === 'sandbox' ? ' (sandbox)' : '';
      if (notify) notify(`📱 STK push sent — awaiting PIN confirmation${modeLabel}`);
    } catch(e) {
      setMpesaStatus({ ok: false, msg: e.response?.data?.error || 'M-Pesa request failed' });
    } finally { setMpesaPending(false); }
  };

  const doStripeTest = async () => {
    setStripePending(true); setStripeStatus(null);
    const cardNum = stripeCard.replace(/\s/g,'');
    try {
      const r = await api.stripePayIntent({ amount: bill.total, bill_id: bill.id, card_number: cardNum });
      const d = r.data;
      if (d.status === 'failed') {
        setStripeStatus({ ok: false, msg: d.message, card: d.card_last4 });
      } else {
        setStripeStatus({ ok: true, data: d });
        setOverrideMode('pdq');
        setOverrideTx(d.payment_intent || 'STRIPE_TEST_OK');
        if (notify) notify(`💳 Stripe test: ${d.description} — card ending ${d.card_last4}`);
      }
    } catch(e) {
      setStripeStatus({ ok: false, msg: e.response?.data?.error || 'Stripe test failed' });
    } finally { setStripePending(false); }
  };

  // Use waiter-submitted values by default
  const finalMode = overrideMode || bill.payment_mode || 'cash';
  const finalTx   = overrideTx   || bill.transaction_code || '';

  const handle = async () => {
    setLoading(true);
    try { await onConfirm(bill.id, finalMode, finalTx); } catch {}
    finally { setLoading(false); }
  };

  const modeColor = MODE_COLORS[finalMode] || '#c8a96e';
  const modeIcon  = MODE_ICONS[finalMode]  || '💰';

  return (
    <div style={{ background:'var(--bgCard)', border:'2px solid #c8a96e', borderRadius:6, padding:20 }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
        <div>
          <div style={{ fontWeight:700, color:'var(--textPrimary)', fontSize:18 }}>Bill #{bill.id}</div>
          <div style={{ fontSize:12, color:'var(--textMuted)', marginTop:3 }}>Table {bill.order_table||`Order #${bill.order}`} · {bill.bill_type}</div>
          <div style={{ fontSize:13, color:'var(--textMuted)', marginTop:5 }}>
            <span style={{ color:'#3498db', fontWeight:700 }}>👨‍🍽️ {bill.waiter_name}</span>
            {bill.guest_name&&bill.guest_name!=='Walk-in'&&<span style={{ color:'var(--textMuted)' }}> · 🏨 {bill.guest_name}</span>}
          </div>
          <div style={{ fontSize:11, color:'var(--textFaint)', marginTop:3 }}>Requested: {bill.requested_at?new Date(bill.requested_at).toLocaleString('en-KE'):'—'}</div>
        </div>
        <div style={{ fontSize:28, fontWeight:700, color:'var(--accent)' }}>{fmtKES(bill.total)}</div>
      </div>

      {/* Items */}
      <div style={{ background:'#0e0e0e', borderRadius:4, padding:'10px 12px', marginBottom:14 }}>
        {(bill.order_items||[]).map(i=>(
          <div key={i.id} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', borderBottom:'1px solid #1a1a1a', fontSize:13 }}>
            <span style={{ color:'var(--textSecond)' }}>{i.name} ×{i.quantity}</span>
            <span style={{ color:'var(--accent)', fontWeight:600 }}>{fmtKES(parseFloat(i.price)*i.quantity)}</span>
          </div>
        ))}
        <div style={{ display:'flex', justifyContent:'space-between', paddingTop:8, borderTop:'1px solid var(--border)', marginTop:4 }}>
          <span style={{ fontWeight:700, color:'var(--textPrimary)' }}>TOTAL</span>
          <span style={{ fontWeight:700, color:'var(--accent)', fontSize:18 }}>{fmtKES(bill.total)}</span>
        </div>
      </div>

      {/* Waiter-submitted payment info */}
      {bill.payment_mode && (
        <div style={{ background:'#0a1a0a', border:`1px solid ${modeColor}55`, borderRadius:4, padding:'10px 14px', marginBottom:14 }}>
          <div style={{ fontSize:11, color:'var(--textFaint)', letterSpacing:1, textTransform:'uppercase', marginBottom:6 }}>Payment Submitted by Waiter</div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:22 }}>{modeIcon}</span>
            <div>
              <div style={{ color:modeColor, fontWeight:700, textTransform:'uppercase', fontSize:14 }}>{bill.payment_mode}</div>
              {bill.transaction_code
                ? <div style={{ fontFamily:'monospace', fontSize:16, letterSpacing:2, color:'#2ecc71', fontWeight:700, marginTop:2 }}>{bill.transaction_code}</div>
                : <div style={{ color:'var(--textFaint)', fontSize:12, marginTop:2 }}>No transaction code (cash)</div>
              }
            </div>
          </div>
        </div>
      )}

      {/* Override section — prominent, always visible */}
      <div style={{ border:'1px solid #2a3a4a', borderRadius:4, overflow:'hidden', marginBottom:12 }}>
        <div style={{ background:'#0d1a2a', padding:'8px 14px', display:'flex', alignItems:'center', gap:8, borderBottom:'1px solid #1e2a3a' }}>
          <span style={{ fontSize:13 }}>🔄</span>
          <div style={{ fontSize:11, color:'#3498db', letterSpacing:1.5, textTransform:'uppercase', fontWeight:700 }}>Cashier Override — Change Payment Details</div>
        </div>
        <div style={{ padding:'12px 14px', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:8 }}>
          <Field label="Override Payment Mode">
            <select style={{ ...inputStyle, border: overrideMode ? '1px solid #3498db' : '1px solid #2a2a2a' }} value={overrideMode} onChange={e=>setOverrideMode(e.target.value)}>
              <option value="">Use waiter's mode ({bill.payment_mode || 'cash'})</option>
              <option value="cash">Cash</option>
              <option value="mpesa">M-Pesa</option>
              <option value="bank">Bank Transfer</option>
              <option value="pdq">PDQ / Card</option>
              <option value="room-charge">Room Charge</option>
              <option value="complimentary">🎁 Complimentary</option>
            </select>
          </Field>
          <Field label="Override Tx / Reference Code">
            <input style={{ ...inputStyle, letterSpacing:1, textTransform:'uppercase', border: overrideTx ? '1px solid #3498db' : '1px solid #2a2a2a' }} value={overrideTx} onChange={e=>setOverrideTx(e.target.value.toUpperCase())} placeholder="Override transaction code…"/>
          </Field>
        </div>
        {(overrideMode || overrideTx) && (
          <div style={{ padding:'6px 14px 10px', background:'#080e18', display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:11 }}>✅</span>
            <span style={{ fontSize:11, color:'#3498db' }}>
              Override active — payment will be recorded as: <strong style={{ color:'#fff' }}>{overrideMode || bill.payment_mode || 'cash'}</strong>
              {overrideTx && <span> · Ref: <strong style={{ color:'#fff', fontFamily:'monospace' }}>{overrideTx}</strong></span>}
            </span>
            <button onClick={() => { setOverrideMode(''); setOverrideTx(''); }} style={{ marginLeft:'auto', background:'#1a0a0a', border:'1px solid #5a2020', borderRadius:3, color:'#e74c3c', fontSize:10, padding:'2px 8px', cursor:'pointer', fontFamily:'inherit' }}>Clear Override</button>
          </div>
        )}
      </div>

      {/* ── Payment Testing Panel ── */}
      <div style={{ marginBottom:12, border:'1px solid #1e2a3a', borderRadius:4, overflow:'hidden' }}>
        <div style={{ display:'flex', background:'#080e18', padding:'8px 12px', gap:6 }}>
          <span style={{ fontSize:11, color:'#3498db', letterSpacing:1, textTransform:'uppercase', marginRight:6, lineHeight:'28px' }}>⚡ Test Payment:</span>
          {['mpesa','stripe'].map(mode => (
            <button key={mode} onClick={()=>setTestMode(testMode===mode?'':mode)}
              style={{ fontSize:11, padding:'4px 12px', borderRadius:3, fontFamily:'inherit', cursor:'pointer', letterSpacing:1, textTransform:'uppercase',
                border:`1px solid ${testMode===mode?(mode==='mpesa'?'#00b300':'#635bff'):'#1e2a3a'}`,
                background:testMode===mode?(mode==='mpesa'?'#003300':'#0d0a2a'):'transparent',
                color:testMode===mode?(mode==='mpesa'?'#00e676':'#7b6ff0'):'#555' }}>
              {mode==='mpesa'?'📱 M-Pesa STK':'💳 Stripe Card'}
            </button>
          ))}
        </div>

        {testMode==='mpesa' && (
          <div style={{ padding:14, background:'#030d03', borderTop:'1px solid #003300' }}>
            <div style={{ fontSize:11, color:'#00b300', letterSpacing:1, textTransform:'uppercase', marginBottom:10 }}>M-Pesa STK Push Simulation</div>
            <div style={{ fontSize:11, color:'#556655', marginBottom:10, lineHeight:1.6 }}>
              Simulates sending an STK push prompt to the customer's phone. In production this calls the Safaricom Daraja API.<br/>
              <span style={{ color:'#00b300' }}>Test numbers: 0712345678 or 254712345678</span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:8, marginBottom:8 }}>
              <div>
                <div style={{ fontSize:10, color:'#556655', letterSpacing:1, marginBottom:4 }}>CUSTOMER PHONE *</div>
                <input style={{ ...inputStyle, letterSpacing:1 }} placeholder="0712 345 678" value={mpesaPhone} onChange={e=>setMpesaPhone(e.target.value)}/>
              </div>
              <div style={{ display:'flex', alignItems:'flex-end' }}>
                <button onClick={doMpesaTest} disabled={mpesaPending || !mpesaPhone}
                  style={{ padding:'10px 18px', background:'#003300', border:'1px solid #00660055', borderRadius:3, color:'#00e676', cursor:'pointer', fontSize:13, fontWeight:700, fontFamily:'inherit', whiteSpace:'nowrap', opacity:!mpesaPhone?0.5:1 }}>
                  {mpesaPending ? '⏳ Sending…' : '📱 Send STK Push'}
                </button>
              </div>
            </div>
            <div style={{ fontSize:11, color:'#445544' }}>Amount: <strong style={{ color:'#00b300' }}>KES {parseFloat(bill.total).toLocaleString('en-KE')}</strong></div>
            {mpesaStatus && (
              <div style={{ marginTop:10, padding:'10px 12px', background: mpesaStatus.ok?'#031203':'#120303', border:`1px solid ${mpesaStatus.ok?'#00660055':'#66000055'}`, borderRadius:3 }}>
                {mpesaStatus.ok ? (
                  <>
                    <div style={{ color:'#00e676', fontWeight:700, marginBottom:4 }}>✓ STK Push Sent (Test)</div>
                    <div style={{ fontFamily:'monospace', fontSize:12, color:'var(--textMuted)' }}>Checkout ID: {mpesaStatus.data.CheckoutRequestID}</div>
                    <div style={{ fontSize:11, color:'#667766', marginTop:4 }}>{mpesaStatus.data.CustomerMessage}</div>
                    <div style={{ fontSize:11, color:'#446644', marginTop:6, fontStyle:'italic' }}>✓ Transaction code auto-filled above — click Confirm to finalize</div>
                  </>
                ) : (
                  <div style={{ color:'#e74c3c' }}>✗ {mpesaStatus.msg}</div>
                )}
              </div>
            )}
          </div>
        )}

        {testMode==='stripe' && (
          <div style={{ padding:14, background:'#03030d', borderTop:'1px solid #1a1a3a' }}>
            <div style={{ fontSize:11, color:'#7b6ff0', letterSpacing:1, textTransform:'uppercase', marginBottom:10 }}>Stripe Payment Simulation</div>
            <div style={{ fontSize:11, color:'#334', marginBottom:10, lineHeight:1.6 }}>
              Test Stripe card processing. In production this calls the Stripe API.<br/>
              <span style={{ color:'#7b6ff0' }}>Use test cards below — real card numbers are never accepted in test mode.</span>
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
              {[
                { num:'4242 4242 4242 4242', label:'✓ Visa Success',    color:'#2ecc71' },
                { num:'4000 0000 0000 0002', label:'✗ Card Declined',   color:'#e74c3c' },
                { num:'4000 0000 0000 9995', label:'✗ Insufficient',    color:'#e67e22' },
                { num:'4000 0025 0000 3155', label:'🔒 3D Secure',      color:'#3498db' },
              ].map(tc => (
                <button key={tc.num} onClick={()=>setStripeCard(tc.num)}
                  style={{ fontSize:10, padding:'4px 10px', borderRadius:3, fontFamily:'monospace', cursor:'pointer', letterSpacing:1,
                    border:`1px solid ${stripeCard===tc.num?tc.color:'#2a2a3a'}`,
                    background:stripeCard===tc.num?`${tc.color}15`:'#0a0a1a',
                    color:stripeCard===tc.num?tc.color:'var(--textFaint)' }}>
                  {tc.label}
                </button>
              ))}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:8, marginBottom:8 }}>
              <div>
                <div style={{ fontSize:10, color:'#445', letterSpacing:1, marginBottom:4 }}>CARD NUMBER</div>
                <input style={{ ...inputStyle, fontFamily:'monospace', letterSpacing:2 }} maxLength={19}
                  value={stripeCard} onChange={e=>setStripeCard(e.target.value)}/>
              </div>
              <div>
                <div style={{ fontSize:10, color:'#445', letterSpacing:1, marginBottom:4 }}>EXPIRY</div>
                <input style={{ ...inputStyle, fontFamily:'monospace' }} placeholder="MM/YY"
                  value={stripeExp} onChange={e=>setStripeExp(e.target.value)}/>
              </div>
              <div>
                <div style={{ fontSize:10, color:'#445', letterSpacing:1, marginBottom:4 }}>CVV</div>
                <input style={{ ...inputStyle, fontFamily:'monospace' }} placeholder="123" maxLength={4}
                  value={stripeCvv} onChange={e=>setStripeCvv(e.target.value)}/>
              </div>
            </div>
            <button onClick={doStripeTest} disabled={stripePending}
              style={{ width:'100%', padding:'10px', background:'#0d0a2a', border:'1px solid #635bff55', borderRadius:3, color:'#7b6ff0', cursor:'pointer', fontSize:13, fontWeight:700, fontFamily:'inherit' }}>
              {stripePending ? '⏳ Processing…' : `💳 Charge KES ${parseFloat(bill.total).toLocaleString('en-KE')} (Test)`}
            </button>
            {stripeStatus && (
              <div style={{ marginTop:10, padding:'10px 12px', background:stripeStatus.ok?'#030d08':'#120303', border:`1px solid ${stripeStatus.ok?'#2ecc7155':'#e74c3c55'}`, borderRadius:3 }}>
                {stripeStatus.ok ? (
                  <>
                    <div style={{ color:'#2ecc71', fontWeight:700, marginBottom:4 }}>✓ Payment Successful (Test)</div>
                    <div style={{ fontFamily:'monospace', fontSize:11, color:'var(--textMuted)' }}>Intent: {stripeStatus.data.payment_intent}</div>
                    <div style={{ fontSize:11, color:'#667766', marginTop:4 }}>{stripeStatus.data.description} · Card ending {stripeStatus.data.card_last4}</div>
                    <div style={{ fontSize:11, color:'#446644', marginTop:6, fontStyle:'italic' }}>✓ Auth code auto-filled above — click Confirm to finalize</div>
                  </>
                ) : (
                  <div style={{ color:'#e74c3c' }}>✗ {stripeStatus.msg} {stripeStatus.card?`(card ••••${stripeStatus.card})`:''}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display:'flex', gap:8 }}>
        <button style={{ ...btnPrimary, background:'#1a6b3a', fontSize:15, flex:1 }} onClick={handle} disabled={loading}>
          {loading?'Confirming…':'✓ Confirm & Mark as Paid'}
        </button>
        <button style={{ background:'#1a2a3a', border:'1px solid #2a3a4a', borderRadius:2, padding:'10px 16px', color:'var(--accent)', fontSize:13, cursor:'pointer' }} onClick={()=>printCashierReceipt(bill)}>🖨</button>
      </div>
    </div>
  );
}

function PoolPanel({ notify, onDone }) {
  const bp = useBreakpoint();
  const [form, setForm] = useState({ visitor_name:'', rate_id:'pr1', quantity:1, guest:'', payment_mode:'cash', transaction_code:'' });
  const [guests, setGuests] = useState([]);
  const [entries, setEntries] = useState([]);

  const loadEntries = useCallback(async () => {
    const r = await api.getPoolEntries(); setEntries(r.data.results || r.data);
    const g = await api.getGuests(); setGuests((g.data.results||g.data).filter(x=>x.status==='checked-in'));
  }, []);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  const RATES = { pr1:['Adult (Day Pass)',800], pr2:['Child (Day Pass)',400], pr3:['Hotel Guest Rate',500] };
  const [, ratePrice] = RATES[form.rate_id] || [,800];
  const total = ratePrice * form.quantity;
  const needsCode = ['mpesa','bank','pdq'].includes(form.payment_mode);

  const process = async () => {
    const guestObj = guests.find(g=>g.id===parseInt(form.guest));
    const name = guestObj?.name || form.visitor_name;
    if (!name) { notify('Enter visitor name','error'); return; }
    if (needsCode && !form.transaction_code.trim()) { notify('Transaction code required','error'); return; }
    await api.createPoolEntry({ ...form, quantity:parseInt(form.quantity) });
    await loadEntries(); onDone();
    setForm({ visitor_name:'', rate_id:'pr1', quantity:1, guest:'', payment_mode:'cash', transaction_code:'' });
    notify(`Pool entry — ${fmtKES(total)} collected!`);
  };

  return (
    <div style={{ display:'grid', gridTemplateColumns: bp === 'sm' ? '1fr' : '420px 1fr', gap:16 }}>
      <Card title="Pool Entry">
        <Field label="Hotel Guest (optional)">
          <select style={inputStyle} value={form.guest} onChange={e=>setForm({...form,guest:e.target.value})}>
            <option value="">— Walk-in —</option>
            {guests.map(g=><option key={g.id} value={g.id}>{g.name} (Rm {g.room_number})</option>)}
          </select>
        </Field>
        {!form.guest && <Field label="Visitor Name"><input style={inputStyle} value={form.visitor_name} onChange={e=>setForm({...form,visitor_name:e.target.value})} placeholder="Full name"/></Field>}
        <Field label="Rate">
          <select style={inputStyle} value={form.rate_id} onChange={e=>setForm({...form,rate_id:e.target.value})}>
            <option value="pr1">Adult (Day Pass) — KES 800</option>
            <option value="pr2">Child (Day Pass) — KES 400</option>
            <option value="pr3">Hotel Guest Rate — KES 500</option>
          </select>
        </Field>
        <Field label="Persons"><input style={inputStyle} type="number" min="1" value={form.quantity} onChange={e=>setForm({...form,quantity:e.target.value})}/></Field>
        <Field label="Payment Mode">
          <select style={inputStyle} value={form.payment_mode} onChange={e=>setForm({...form,payment_mode:e.target.value,transaction_code:''})}>
            <option value="cash">💵 Cash</option><option value="mpesa">📱 M-Pesa</option><option value="bank">🏦 Bank</option><option value="pdq">💳 PDQ/Card</option><option value="room-charge">🏨 Room Charge</option><option value="complimentary">🎁 Complimentary</option>
          </select>
        </Field>
        {needsCode && <Field label={form.payment_mode==='mpesa'?'M-Pesa Code *':form.payment_mode==='bank'?'Bank Ref *':'PDQ Auth Code *'}>
          <input style={{ ...inputStyle, letterSpacing:2, textTransform:'uppercase', fontWeight:700, color:'#2ecc71' }} value={form.transaction_code} onChange={e=>setForm({...form,transaction_code:e.target.value.toUpperCase()})} placeholder="Enter code…"/>
        </Field>}
        <div style={{ background:'var(--bgInput)', border:'1px solid #2a2a2a', borderRadius:3, padding:'9px 12px', marginBottom:10, fontSize:13 }}>
          {RATES[form.rate_id]?.[0]} × {form.quantity} = <strong style={{ color:'var(--accent)' }}>{fmtKES(total)}</strong>
        </div>
        <button style={{ ...btnPrimary, background:'#1a6b9e' }} onClick={process}>🏊 Process Pool Entry</button>
      </Card>
      <Card title="Pool Entries Today">
        {entries.length===0?<Empty text="No pool entries today"/>:<Table
          headers={['Visitor','Rate','Qty','Total','Mode','Tx Code','Time']}
          rows={[...entries].reverse().map(e=>[
            e.visitor_name, e.rate_type, e.quantity,
            <span style={{ color:'#2ecc71', fontWeight:700 }}>{fmtKES(e.total)}</span>,
            <span style={{ textTransform:'uppercase', fontSize:11 }}>{e.payment_mode}</span>,
            e.payment?.transaction_code?<code style={{ fontSize:11, color:'var(--accent)' }}>{e.payment.transaction_code}</code>:<span style={{ color:'var(--textFaint)' }}>—</span>,
            <span style={{ fontSize:11, color:'var(--textFaint)' }}>{new Date(e.created_at).toLocaleString('en-KE')}</span>
          ])}
        />}
      </Card>
    </div>
  );
}

// ─── Complimentary Bills Panel ─────────────────────────────────────────────────
const COMP_CATEGORIES = [
  { v:'vip_guest',  label:'VIP Guest' },
  { v:'complaint',  label:'Complaint Resolution' },
  { v:'staff_meal', label:'Staff Meal' },
  { v:'marketing',  label:'Marketing / Promotion' },
  { v:'error',      label:'Billing Error' },
  { v:'other',      label:'Other' },
];

function ComplimentaryPanel({ compBills, bills, notify, onUpdate, canApprove }) {
  const [mode, setMode] = useState('list'); // 'list' | 'new'
  const [form, setForm] = useState({ guest_name:'', category:'other', reason:'', bill:'', amount:'' });
  const [loading, setLoading] = useState(false);

  const pendingBills = bills.filter(b => b.status === 'pending' || b.status === 'payment-requested');

  const statusColor = s => s==='approved'?'#2ecc71':s==='rejected'?'#e74c3c':'#c8a96e';

  const selectBill = (billId) => {
    const b = bills.find(b=>b.id===parseInt(billId));
    if (b) setForm(f => ({ ...f, bill:billId, guest_name:b.guest_name||'', amount:b.total }));
    else setForm(f => ({ ...f, bill:billId }));
  };

  const submit = async () => {
    if (!form.guest_name.trim()) { notify('Enter guest name', 'error'); return; }
    if (!form.reason.trim())     { notify('Provide a reason', 'error'); return; }
    if (!form.amount || parseFloat(form.amount)<=0) { notify('Enter amount', 'error'); return; }
    setLoading(true);
    try {
      await api.createComplimentaryBill({
        guest_name: form.guest_name,
        category:   form.category,
        reason:     form.reason,
        amount:     parseFloat(form.amount),
        bill:       form.bill ? parseInt(form.bill) : null,
      });
      notify('Complimentary bill submitted for manager approval!');
      onUpdate();
      setMode('list');
      setForm({ guest_name:'', category:'other', reason:'', bill:'', amount:'' });
    } catch(e) { notify(e.response?.data?.error || 'Failed to submit', 'error'); }
    finally { setLoading(false); }
  };

  const approve = async (id) => {
    try { await api.approveComplimentaryBill(id); notify('Complimentary bill approved & expensed!'); onUpdate(); }
    catch(e) { notify(e.response?.data?.error || 'Approval failed', 'error'); }
  };

  const reject = async (id) => {
    const reason = window.prompt('Rejection reason (optional):') || '';
    try { await api.rejectComplimentaryBill(id, { reason }); notify('Complimentary bill rejected'); onUpdate(); }
    catch { notify('Failed', 'error'); }
  };

  const totalComp = compBills.filter(c=>c.status==='approved').reduce((s,c)=>s+parseFloat(c.amount||0),0);

  return (
    <div>
      {/* Summary bar */}
      <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:12,marginBottom:16 }}>
        {[
          { label:'Total Requests',   value:compBills.length,                               color:'#3498db' },
          { label:'Pending Approval', value:compBills.filter(c=>c.status==='pending').length, color:'var(--accent)' },
          { label:'Approved',         value:compBills.filter(c=>c.status==='approved').length,color:'#2ecc71' },
          { label:'Total Expensed',   value:fmtKES(totalComp),                               color:'#e74c3c' },
        ].map(s=>(
          <div key={s.label} style={{ background:'var(--bgInput)',border:`1px solid ${s.color}33`,borderTop:`3px solid ${s.color}`,borderRadius:6,padding:'12px 16px',textAlign:'center' }}>
            <div style={{ fontSize:20,fontWeight:800,color:s.color }}>{s.value}</div>
            <div style={{ fontSize:11,color:'var(--textMuted)',marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'flex',gap:10,marginBottom:14 }}>
        <button style={btnPrimary} onClick={() => setMode(mode==='new'?'list':'new')}>
          {mode==='new' ? '← Back to List' : '＋ New Complimentary Bill'}
        </button>
        {!canApprove && (
          <div style={{ background:'#1a0a0a',border:'1px solid #c8a96e44',borderRadius:4,padding:'6px 14px',fontSize:12,color:'var(--accent)',display:'flex',alignItems:'center' }}>
            ℹ️ Complimentary bills require manager approval
          </div>
        )}
      </div>

      {mode === 'new' && (
        <Card title="New Complimentary Bill">
          <div style={{ background:'#0a1a0a',border:'1px solid #2ecc7133',borderRadius:4,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#668866' }}>
            🎁 A complimentary bill waives the guest's charges. Once approved by the manager, it is automatically recorded as a hotel expense in accounting.
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:12 }}>
            <Field label="Attach to Existing Bill (optional)">
              <select style={inputStyle} value={form.bill} onChange={e=>selectBill(e.target.value)}>
                <option value="">— No existing bill (manual entry) —</option>
                {pendingBills.map(b=>(
                  <option key={b.id} value={b.id}>
                    Bill #{b.id} — {b.guest_name} — {fmtKES(b.total)} ({b.bill_type})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Guest Name *">
              <input style={inputStyle} value={form.guest_name} onChange={e=>setForm({...form,guest_name:e.target.value})} placeholder="Full name" />
            </Field>
            <Field label="Category *">
              <select style={inputStyle} value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
                {COMP_CATEGORIES.map(c=><option key={c.v} value={c.v}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Amount (KES) *">
              <input style={{ ...inputStyle,color:'var(--accent)',fontWeight:700 }} type="number" min="0" step="0.01"
                value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} placeholder="0.00" />
            </Field>
            <Field label="Reason / Justification *" style={{ gridColumn:'1/-1' }}>
              <textarea style={{ ...inputStyle,height:80,resize:'none' }}
                value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})}
                placeholder="Explain why this bill is being comped (e.g. VIP guest, complaint resolution, service failure…)" />
            </Field>
          </div>
          <div style={{ display:'flex',gap:8,marginTop:12 }}>
            <button style={{ ...btnSm,flex:1 }} onClick={()=>setMode('list')}>← Cancel</button>
            <button style={{ ...btnPrimary,flex:2,background:'var(--accent)',color:'#111' }} disabled={loading} onClick={submit}>
              {loading ? 'Submitting…' : '📤 Submit for Manager Approval'}
            </button>
          </div>
        </Card>
      )}

      {mode === 'list' && (
        compBills.length === 0 ? <Empty text="No complimentary bills yet" /> : (
          <div style={{ display:'grid',gap:10 }}>
            {compBills.map(cb => {
              const sc = statusColor(cb.status);
              return (
                <div key={cb.id} style={{ background:'var(--bgInput)',border:`1px solid ${sc}33`,borderLeft:`4px solid ${sc}`,borderRadius:4,padding:'14px 16px',display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10 }}>
                  <div>
                    <div style={{ fontWeight:700,color:'var(--textPrimary)',fontSize:14,display:'flex',gap:8,alignItems:'center' }}>
                      🎁 {cb.ref}
                      <span style={{ fontSize:10,fontWeight:600,padding:'2px 8px',borderRadius:3,background:`${sc}22`,color:sc,textTransform:'uppercase' }}>{cb.status}</span>
                    </div>
                    <div style={{ fontSize:13,color:'var(--textSecond)',marginTop:4 }}>
                      <strong>{cb.guest_name}</strong>
                      <span style={{ color:'var(--textMuted)',marginLeft:8 }}>· {cb.category_label}</span>
                    </div>
                    <div style={{ fontSize:12,color:'var(--textMuted)',marginTop:4,maxWidth:500 }}>{cb.reason}</div>
                    <div style={{ fontSize:11,color:'var(--textMuted)',marginTop:4,display:'flex',gap:12 }}>
                      <span style={{ color:'#e74c3c',fontWeight:700 }}>KES {parseFloat(cb.amount).toLocaleString('en-KE',{minimumFractionDigits:2})}</span>
                      <span>By: {cb.requested_by_name}</span>
                      <span>{new Date(cb.created_at).toLocaleDateString('en-KE')}</span>
                      {cb.approved_by_name && cb.approved_by_name!=='—' && <span style={{ color:'#2ecc71' }}>✓ {cb.approved_by_name}</span>}
                      {cb.expense && <span style={{ color:'#3498db' }}>📊 Expensed</span>}
                    </div>
                    {cb.rejection_reason && <div style={{ marginTop:4,fontSize:11,color:'#e74c3c' }}>Rejected: {cb.rejection_reason}</div>}
                  </div>
                  {canApprove && cb.status==='pending' && (
                    <div style={{ display:'flex',gap:8 }}>
                      <button style={{ ...btnSm,background:'#0a1a0a',color:'#2ecc71',border:'1px solid #2ecc7144' }}
                        onClick={()=>approve(cb.id)}>✓ Approve &amp; Expense</button>
                      <button style={{ ...btnSm,background:'#1a0a0a',color:'#e74c3c',border:'1px solid #e74c3c44' }}
                        onClick={()=>reject(cb.id)}>✕ Reject</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
